import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, handleAxiosError, TipoEmpleadoEnumChoice } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
  AlertSeverityEnum,
  UseFetchEnabledParams,
  UseMutationsParams,
} from '@/shared/interfaces';
import {
  Empleado,
  EmpleadosPagingRes,
} from '@/shared/interfaces/erp/empleados';
import { useUiStore } from '@/store/ui';

const { get, post, put, remove } = erpApi();

export const useFetchEmpleados = ({
  enabled = true,
  params,
}: UseFetchEnabledParams<GetEmpleadosParams> = {}) => {
  return useQuery({
    queryKey: ['empleados', ...Object.values(params ?? {})],
    queryFn: () => getEmpleados(params),
    enabled,
    refetchOnWindowFocus: false,
  });
};

export const useGetEmpleado = (id: string) => {
  return useQuery({
    queryKey: ['empleado', id],
    queryFn: () => getEmpleado(id),
    retry: false,
  });
};

export const useCreateEmpleado = <T>({
  navigate,
  enableRedirect = true,
  returnUrl,
  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,
  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: (params: CreateEmpleadoParams<T>) => createEmpleado(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['empleados'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Empleado creado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));
      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateEmpleado = <T>({
  navigate,
  enableRedirect = true,
  returnUrl,
  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,
  enableSuccessAlert = true,
  customSuccessMessage,
  customSuccessCallback,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: (params: UpdateEmpleadoParams<T>) => updateEmpleado(params),
    onSuccess: res => {
      queryClient.invalidateQueries({ queryKey: ['empleados'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Empleado actualizado correctamente',
          AlertSeverityEnum.success
        );

      customSuccessCallback && customSuccessCallback(res?.data);
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));
      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useDeleteEmpleado = () => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;

  return useMutation({
    mutationFn: deleteEmpleado,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['empleados'] });
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
    onError: error => {
      handleAxiosError(error);
    },
  });
};

export type GetEmpleadosParams = Partial<Empleado> & {
  page?: number;
  page_size?: number;
  filterByState?: boolean;
  identificacion?: string;
  canales_venta?: string | null;

};

export type CreateEmpleadoParamsForm = Omit<Empleado, 'id_empleado'>;

export type CreateEmpleadoParams<T> = T;
export type UpdateEmpleadoParams<T> = {
  id: string;
  data: T;
};

export const getEmpleados = async (params?: GetEmpleadosParams) => {
  const stateParams = { ...params };

  // filterByState
  if (stateParams.filterByState === false && stateParams.estado === undefined) {
    delete stateParams.estado;
  } else if (stateParams.filterByState !== false) {
    stateParams.estado = true;
  }
  delete stateParams.filterByState;

  const queryParams = getUrlParamsFetch(stateParams);

  return get<EmpleadosPagingRes>(
    `${urlBackend}api/v1/empleado/?${queryParams}`,
    true
  );
};

export const getEmpleado = async (id: string) => {
  try {
    return await get<Empleado>(`${urlBackend}api/v1/empleado/${id}`, true);
  } catch (error) {
    handleAxiosError(error);
  }
};

export const createEmpleado = async <T>(data: CreateEmpleadoParams<T>) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<Empleado>(`${urlBackend}api/v1/empleado/`, data, true);
};

export const updateEmpleado = async <T>({
  id,
  data,
}: UpdateEmpleadoParams<T>) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<Empleado>(`${urlBackend}api/v1/empleado/${id}/`, data, true);
};

export const deleteEmpleado = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;

  setIsGlobalLoading(true);
  const res = await remove<Empleado>(
    `${urlBackend}api/v1/empleado/${id}/`,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};


///* fetch para el autcomplete

export const fetchAutocompleteVendedores = async (
  searchValue: string,
  page: number,
  queryOptional: any = {}
): Promise<{ options: Empleado[]; hasMore: boolean }> => {
  const query = getUrlParamsFetch({
    ...queryOptional,
    estado: true,
    tipo_empleado: TipoEmpleadoEnumChoice.VENDEDOR,
    razon_social: searchValue,
    page,
  });

  const response = await get<EmpleadosPagingRes>(`${urlBackend}api/v1/empleado/?${query}`, true);

  const data = response.data.data;

  const dataEmpleadoTest = {
    "id_empleado": "c05c0260-5707-4c26-af42-1320c48b8a46",
    "estado": true,
    "codigo": "VER TODO",
    "numero_referencia": "V-1822",
    "tipo_identificacion": "CÉDULA",
    "identificacion": "1714006382",
    "razon_social": "VER TODO",
    "direccion": "-",
    "email": "ventas19@fibramax.ec",
    "telefono_1": "0999999999",
    "telefono_2": "0999999999",
    "telefono_3": "0999999999",
    "tipo_empleado": "VENDEDOR",
    "pais": "EC",
    "provincia": "EC-19",
    "ciudad": "EC-19-01",
    "parroquia": "null",
    "zona": "ZONA649ed293-c622-4e2f-9c99-63eb765517f9",
    "sector": "CONDADO",
    "empresa": "FIBRAMAX",
    "area": "COM-01",
    "departamento": "DEPA1b69e2ea-c06a-4814-9c6c-92f502219078",
    "user": "null",
    "cargo": "CARGO-01"
  }

  const xd = [dataEmpleadoTest, ...data];


  return {
    options: xd,
    hasMore: true,
  };
};